import { Badge, Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui';
import { PromotionStatusEnum } from '@/enums';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Megaphone, Tag } from 'lucide-react';
import type { IPromotion } from '@/interfaces';
import {
	PROMOTION_PRODUCT_LABELS,
	PROMOTION_STATUS_CONFIG,
	PROMOTION_PRODUCT_PACKAGES,
} from '@/constants';
import { formatPromotionPackageValue } from '@/functions';

const formatDate = (d: string) => {
	try {
		return format(new Date(d), 'dd MMM yyyy HH:mm', { locale: fr });
	} catch {
		return d;
	}
};

type TRow = { label: string; value: React.ReactNode };

const DetailRow = ({ label, value }: TRow) => (
	<div className="flex justify-between items-center gap-2">
		<span className="text-sm text-muted-foreground whitespace-nowrap">{label}</span>
		<span className="text-sm font-medium text-right">{value}</span>
	</div>
);

type TProps = {
	promotion: IPromotion | null;
	isOpen: boolean;
	onClose: () => void;
};

const PromotionDetailsModal = ({ promotion, isOpen, onClose }: TProps) => {
	if (!promotion) return null;

	const statusCfg =
		PROMOTION_STATUS_CONFIG[promotion.status] ??
		PROMOTION_STATUS_CONFIG[PromotionStatusEnum.PENDING];

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent
				showCloseIcon
				onClickCloseIcon={onClose}
				className="flex flex-col p-0 gap-0 max-w-lg w-[calc(100%-1.5rem)] sm:w-full max-h-[90vh] rounded-2xl border bg-background shadow-lg overflow-hidden"
			>
				<DialogHeader className="px-6 pt-6 pb-0">
					<DialogTitle className="flex items-center gap-2 text-xl">
						<Megaphone className="h-5 w-5 text-primary" />
						Détails de la promotion
					</DialogTitle>
				</DialogHeader>

				<div className="px-6 py-6 overflow-y-auto">
					<div className="grid grid-cols-1 gap-8">
						{/* Configuration & Details Column */}
						<div className="flex flex-col gap-6">
							{/* Infos principales */}
							<div className="space-y-3">
								<DetailRow
									label="Statut"
									value={
										<Badge className={cn('text-xs font-medium', statusCfg.className)}>
											{statusCfg.label}
										</Badge>
									}
								/>
								<DetailRow
									label="Bonus"
									value={
										<span className="text-base font-bold text-primary">
											{promotion.percentage}%
										</span>
									}
								/>
								<DetailRow
									label="Produits"
									value={
										<div className="flex flex-wrap gap-1 justify-end">
											{promotion.products.map(p => (
												<Badge key={p} variant="secondary" className="text-xs">
													{PROMOTION_PRODUCT_LABELS[p] ?? p}
												</Badge>
											))}
										</div>
									}
								/>
								<DetailRow label="Début" value={formatDate(promotion.from)} />
								<DetailRow label="Fin" value={formatDate(promotion.to)} />
								{(promotion.title || promotion.description) && (
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
										{(['fr', 'en'] as const).map(lang => {
											const title = promotion.title?.[lang];
											const desc = promotion.description?.[lang];
											if (!title && !desc) return null;
											return (
												<div
													key={lang}
													className="flex flex-col gap-1.5 rounded-lg border bg-muted/20 px-3 py-2 min-w-0"
												>
													<span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
														{lang}
													</span>
													{title && (
														<span className="text-sm font-medium wrap-break-word">
															{title}
														</span>
													)}
													{desc && (
														<span className="text-xs text-muted-foreground wrap-break-word">
															{desc}
														</span>
													)}
												</div>
											);
										})}
									</div>
								)}
							</div>

							<div className="border-t" />

							{/* Bouton principal */}
							{promotion.primaryBtn && (
								<div className="space-y-3">
									<div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
										<Tag className="h-3.5 w-3.5" />
										Bouton principal
									</div>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
										{promotion.primaryBtn.text.fr && (
											<DetailRow
												label="Texte (FR)"
												value={promotion.primaryBtn.text.fr}
											/>
										)}
										{promotion.primaryBtn.text.en && (
											<DetailRow label="Text (EN)" value={promotion.primaryBtn.text.en} />
										)}
									</div>
								</div>
							)}

							{/* Aperçu bonus (Review) - Match form logic */}
							{promotion.percentage > 0 && promotion.products.length > 0 && (
								<div className="rounded-xl border border-dashed bg-muted/40 px-4 py-4 flex flex-col gap-3">
									<p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
										Aperçu (+{promotion.percentage}%)
									</p>
									{promotion.products
										.filter(p => PROMOTION_PRODUCT_PACKAGES[p])
										.map(p => {
											const pkgs = PROMOTION_PRODUCT_PACKAGES[p];
											const pct = promotion.percentage;
											const label = PROMOTION_PRODUCT_LABELS[p] ?? p;
											return (
												<div key={p} className="flex flex-col gap-1.5">
													<p className="text-xs font-semibold">{label}</p>
													<div className="flex flex-wrap gap-2">
														{pkgs?.map(pkg => {
															const newVal = pkg.baseValue * (1 + pct / 100);
															return (
																<span
																	key={pkg.name}
																	className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-2.5 py-1 text-xs"
																>
																	<span className="text-muted-foreground">
																		{pkg.name}
																	</span>
																	{pkg.unit === 'coins' && (
																		<>
																			<span className="text-muted-foreground/40">·</span>
																			<span className="text-muted-foreground/60">
																				{formatPromotionPackageValue(
																					pkg.baseValue,
																					pkg.unit,
																				)}{' '}
																				🪙
																			</span>
																		</>
																	)}
																	<span className="text-muted-foreground/30">➔</span>
																	<span className="font-bold text-primary">
																		{formatPromotionPackageValue(newVal, pkg.unit)}
																		{pkg.unit === 'coins' ? ' 🪙' : ''}
																	</span>
																</span>
															);
														})}
													</div>
												</div>
											);
										})}
								</div>
							)}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default PromotionDetailsModal;
